import { capitalizedWord } from "@pages/SeoPages/helper";

const LOCATION_KEY_LABELS: Record<string, string> = {
  lekki: "Lekki",
  "victoria+island": "Victoria Island",
  victoriaisland: "Victoria Island",
  surulere: "Surulere",
  yaba: "Yaba",
};

const PRICE_KEY_LABELS: Record<string, string> = {
  below1m: "under ₦1M",
  above1m: "above ₦1M",
};

export const isExplorePriceKey = (key: string | null) =>
  key != null && key in PRICE_KEY_LABELS;

const formatKeyToLabel = (key: string) => {
  const normalized = key.toLowerCase();
  return LOCATION_KEY_LABELS[normalized] || capitalizedWord(normalized);
};

const getIntentLabel = (intent: string) => {
  switch (intent) {
    case "shortlet":
      return "Shortlet Properties";
    case "long lease":
      return "Property for Rent";
    case "sale":
      return "Property for Sale";
    default:
      return "Properties";
  }
};

const formatPriceLabel = (value: string) => {
  const amount = Number(value);
  if (Number.isNaN(amount)) return value;

  if (amount >= 1_000_000) {
    const millions = amount / 1_000_000;
    return millions % 1 === 0 ? `₦${millions}M` : `₦${millions.toFixed(1)}M`;
  }

  return `₦${amount.toLocaleString()}`;
};

export const getExploreListingTitle = (
  intent: string,
  searchParams: URLSearchParams,
  filterLabel?: string,
) => {
  const intentLabel = getIntentLabel(intent);

  if (filterLabel) {
    const normalized = filterLabel.toLowerCase().replace(/\s+/g, "");

    if (LOCATION_KEY_LABELS[normalized]) {
      return `${intentLabel} in ${LOCATION_KEY_LABELS[normalized]}`;
    }

    if (Object.values(LOCATION_KEY_LABELS).includes(filterLabel)) {
      return `${intentLabel} in ${filterLabel}`;
    }

    if (filterLabel === "1M & Below") {
      return `${intentLabel} under ₦1M`;
    }

    if (filterLabel === "1M & Above") {
      return `${intentLabel} above ₦1M`;
    }
  }

  const key = searchParams.get("key");
  if (key === "below1m") {
    return `${intentLabel} under ₦1M`;
  }

  if (key === "above1m") {
    return `${intentLabel} above ₦1M`;
  }

  if (key) {
    return `${intentLabel} in ${formatKeyToLabel(key)}`;
  }

  const priceLt = searchParams.get("price[lt]");
  if (priceLt) {
    return `${intentLabel} under ${formatPriceLabel(priceLt)}`;
  }

  const priceGt = searchParams.get("price[gt]");
  if (priceGt) {
    return `${intentLabel} above ${formatPriceLabel(priceGt)}`;
  }

  return null;
};

export const getExploreBlogHeading = (
  intent: string,
  searchParams: URLSearchParams,
  filterLabel?: string,
) => {
  const listingTitle = getExploreListingTitle(intent, searchParams, filterLabel);

  if (!listingTitle) {
    return {
      title: "Blog Posts",
      text: "Latest insights and guides",
    };
  }

  return {
    title: "Guides & Blog Posts",
    text: `Learn more about ${listingTitle.toLowerCase()}`,
  };
};

export const getExploreArticleKey = (
  searchParams: URLSearchParams,
  filterLabel?: string,
) => {
  const key =
    searchParams.get("key") ||
    (filterLabel
      ? Object.entries(LOCATION_KEY_LABELS).find(
          ([, label]) => label === filterLabel,
        )?.[0]
      : undefined);

  if (!key || key in PRICE_KEY_LABELS) {
    return undefined;
  }

  return key.toLowerCase();
};

export const normalizeExploreQueryParams = (
  searchParams: URLSearchParams,
) => {
  const params: Record<string, string> = {};

  searchParams.forEach((value, key) => {
    if (key === "key") {
      if (value === "below1m") {
        params["price[lt]"] = "1000000";
        return;
      }

      if (value === "above1m") {
        params["price[gt]"] = "1000000";
        return;
      }

      params["key"] = value;
      return;
    }

    params[key] = value;
  });

  return params;
};
